import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api/client';
import { Field } from './Field';
import {
  buildSearchUrl,
  makeRequestGate,
  navigableCount,
  resolveEnter,
  stepActive,
} from './entityPicker.logic';

/**
 * EntityPicker<T> (R4) — the ONE search/typeahead primitive. Every entity search in the app is a
 * *configuration* of this, never a reimplementation: it bakes in the debounce, the stale-request guard
 * (fixes the races the hand-rolled ones had), keyboard nav (↑↓/Enter/Esc), min-chars, the empty state,
 * a shared dropdown, click-outside-to-close, and an optional «+ افزودن» create row (the vehicle for R3's
 * inline add). Point it at an `endpoint` (with an optional `queryParam`/`extraParams`) or give it a
 * custom `fetchFn`; describe each row via `renderResult`; handle the pick in `onSelect`.
 *
 * The load-bearing logic lives in entityPicker.logic.ts (pure, unit-tested); this is the thin shell.
 */

/** How one result renders: a required label plus an optional subtitle and leading icon. */
export interface EntityRow {
  label: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
}

export interface EntityPickerProps<T> {
  /** Endpoint mode: GET `${endpoint}?${queryParam}=<q>` (+ extraParams). Ignored if `fetchFn` is set. */
  endpoint?: string;
  queryParam?: string;
  extraParams?: Record<string, string | number>;
  /** Custom source (takes precedence over `endpoint`). Should return the raw result list. */
  fetchFn?: (query: string) => Promise<T[]>;

  /** Stable key for a result (defaults to its index — pass this whenever results have real ids). */
  itemKey?: (item: T) => string;
  /** Describe how a result row looks. */
  renderResult: (item: T) => EntityRow;
  /** Called with the chosen entity. */
  onSelect: (item: T) => void;

  label?: string;
  placeholder?: string;
  minChars?: number;
  debounceMs?: number;
  maxResults?: number;
  /** Clear the input + results after a pick (default true). */
  clearAfterSelect?: boolean;
  autoFocus?: boolean;
  /** Empty-state text when a query returns nothing (default «چیزی یافت نشد»). */
  emptyText?: ReactNode;
  /** Show a trailing create row while typing — the hook for inline «+ افزودن» (R3). */
  allowCreate?: { render: (query: string) => ReactNode; onCreate: (query: string) => void };
}

export function EntityPicker<T>({
  endpoint,
  queryParam = 'q',
  extraParams,
  fetchFn,
  itemKey,
  renderResult,
  onSelect,
  label,
  placeholder = 'برای جستجو تایپ کنید…',
  minChars = 2,
  debounceMs = 200,
  maxResults = 8,
  clearAfterSelect = true,
  autoFocus = false,
  emptyText = 'چیزی یافت نشد',
  allowCreate,
}: EntityPickerProps<T>) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const gate = useRef(makeRequestGate()).current;

  // Inline callers pass fresh fetchFn/extraParams objects each render; read them from refs so the search
  // effect can depend on primitives only and not re-fire on every parent render.
  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;
  const extraRef = useRef(extraParams);
  extraRef.current = extraParams;

  // Debounced, race-guarded fetch.
  useEffect(() => {
    const query = q.trim();
    if (query.length < minChars) {
      setResults([]);
      setOpen(false);
      return;
    }
    const t = setTimeout(async () => {
      const token = gate.issue();
      let rows: T[] = [];
      const customFetch = fetchRef.current;
      if (customFetch) {
        rows = await customFetch(query);
      } else if (endpoint) {
        const r = await api<T[]>(buildSearchUrl(endpoint, queryParam, query, extraRef.current));
        if (r.ok && Array.isArray(r.body)) rows = r.body as T[];
      }
      if (!gate.isLatest(token)) return; // a newer keystroke already fired — drop this stale response
      setResults(rows.slice(0, maxResults));
      setActive(0);
      setOpen(true);
    }, debounceMs);
    return () => clearTimeout(t);
  }, [q, endpoint, queryParam, minChars, debounceMs, maxResults, gate]);

  // Click-outside closes the dropdown (Esc is handled on the input).
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDocMouseDown);
    return () => window.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const hasCreateRow = !!allowCreate && q.trim().length >= minChars;
  const total = navigableCount(results.length, hasCreateRow);

  const reset = () => {
    if (clearAfterSelect) {
      setQ('');
      setResults([]);
    }
    setOpen(false);
  };
  const pick = (item: T) => {
    onSelect(item);
    reset();
  };
  const create = () => {
    if (!allowCreate) return;
    allowCreate.onCreate(q.trim());
    reset();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return setOpen(false);
    if (!open || total === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => stepActive(a, 1, total));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => stepActive(a, -1, total));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const action = resolveEnter(active, results.length, hasCreateRow);
      if (action.kind === 'select') pick(results[action.index] as T);
      else if (action.kind === 'create') create();
    }
  };

  const keyOf = (item: T, i: number) => (itemKey ? itemKey(item) : String(i));
  const showPop = open && q.trim().length >= minChars;

  const input = (
    <input
      className="input"
      value={q}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onChange={(e) => setQ(e.target.value)}
      onFocus={() => results.length && setOpen(true)}
      onKeyDown={onKeyDown}
      role="combobox"
      aria-expanded={showPop}
      aria-autocomplete="list"
    />
  );

  return (
    <div className="epick" ref={boxRef}>
      {label ? <Field label={label}>{input}</Field> : input}
      {showPop ? (
        <div className="epick-pop" role="listbox">
          {results.length === 0 && !hasCreateRow ? (
            <div className="epick-empty">{emptyText}</div>
          ) : (
            <>
              {results.map((item, i) => {
                const row = renderResult(item);
                return (
                  <button
                    key={keyOf(item, i)}
                    type="button"
                    role="option"
                    aria-selected={i === active}
                    className={`epick-item${i === active ? ' active' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(item)}
                  >
                    {row.icon ? <span className="epick-ico">{row.icon}</span> : null}
                    <span className="epick-text">
                      <span className="epick-title">{row.label}</span>
                      {row.subtitle ? <span className="epick-sub">{row.subtitle}</span> : null}
                    </span>
                  </button>
                );
              })}
              {hasCreateRow ? (
                <button
                  type="button"
                  role="option"
                  aria-selected={active === results.length}
                  className={`epick-item epick-create${active === results.length ? ' active' : ''}`}
                  onMouseEnter={() => setActive(results.length)}
                  onClick={create}
                >
                  {allowCreate?.render(q.trim())}
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
